import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { setPageMeta } from "@/lib/api";
import { Logo } from "@/components/Navbar";
import { Splatter } from "@/components/Splatter";
import ProductCard from "@/components/ProductCard";
import { HomeVideo } from "@/components/HomeVideo";
import { Sparkles, Layers, Cog, Boxes, Upload, FileText, ArrowRight, Phone, CheckCircle2, MapPin, Clock, Type } from "lucide-react";

const CAT_META = {
  "lithophanies": { icon: Layers, img: "/images/lithophane_lamp.jpeg", desc: "Vos photos en lampes lumineuses" },
  "figurines": { icon: Sparkles, img: "/images/fig_sangoku.jpg", desc: "Personnages & créatures détaillés" },
  "ecriture-3d": { icon: Type, img: "/images/ecriture_3d.jpg", desc: "Lettres 3D à coller — boîte aux lettres, murs…" },
  "objets-personnalises": { icon: Boxes, img: "/images/custom_keychain.jpeg", desc: "Cadeaux & objets sur mesure" },
  "pieces-techniques": { icon: Cog, img: "https://images.unsplash.com/photo-1740625940423-a59a65c753c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", desc: "Prototypes & pièces fonctionnelles" },
  "impression-fichier-3d": { icon: Upload, img: "https://images.unsplash.com/photo-1611117775350-ac3950990985?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", desc: "Envoyez votre STL, on imprime", to: "/impression-fichier-3d" },
  "demande-de-devis": { icon: FileText, img: "https://images.unsplash.com/photo-1642969164999-979483e21601?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", desc: "Projet spécial ? Demandez un devis", to: "/devis" },
};

const MAPS_URL = "https://www.google.com/maps/dir/?api=1&destination=" + encodeURIComponent("87 rue Centrale, 01230 Tenay");

function computeOpenNow() {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Paris", weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date());
    const wd = parts.find((p) => p.type === "weekday").value;
    const hh = parseInt(parts.find((p) => p.type === "hour").value, 10);
    const mm = parseInt(parts.find((p) => p.type === "minute").value, 10);
    const minutes = hh * 60 + mm;
    return wd !== "Sun" && minutes >= 9 * 60 && minutes < 19 * 60;
  } catch {
    return false;
  }
}

const FRESQUE = [
  "/images/fresque_1.jpg",
  "/images/fresque_2.jpg",
  "/images/fresque_3.jpg",
  "/images/fresque_4.jpg",
  "/images/fresque_5.jpg",
];

const ARGUMENTS = [
  "Devis pour votre création personnalisée ou à partir de votre fichier 3D",
  "Travail soigné et de qualité",
  "Petites ou grandes séries sur demande",
  "Optimisation du coût de revient",
  "Délais prédéfinis et respectés",
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [cats, setCats] = useState([]);
  const [slide, setSlide] = useState(0);
  const [openNow, setOpenNow] = useState(() => computeOpenNow());

  useEffect(() => {
    const t = setInterval(() => setOpenNow(computeOpenNow()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % 2), 4000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    setPageMeta("Tenay Ain-Pression — Impression 3D & créations personnalisées",
      "À Tenay, vos idées prennent vie. Impression 3D, lithophanies, figurines, objets personnalisés et pièces techniques.");
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 8))).catch(() => {});
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <Splatter />
        <div
          className="absolute inset-0 opacity-40 bg-cover bg-center"
          style={{ backgroundImage: "url(/images/home_bg.jpg)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/75 to-black" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24 sm:py-32 text-center">
          <p className="inline-flex items-center gap-2 rounded-full border border-[#E6007E]/40 bg-black/40 px-4 py-1.5 text-sm font-semibold text-[#E6007E]">
            Impression 3D · Création · Innovation
          </p>
          <div className="mt-6">
            <Logo className="text-4xl sm:text-6xl lg:text-7xl" />
          </div>
          <h1 className="mt-8 font-heading text-3xl sm:text-5xl font-black tracking-tight">
            À <span className="text-[#E6007E] text-glow-fuchsia">Tenay</span>, vos idées <span className="text-[#0055FF]">prennent vie</span>
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-gray-300 text-base sm:text-lg">
            Lithophanies, figurines, objets personnalisés et pièces techniques.
            Envoyez votre fichier 3D ou demandez un devis pour votre projet sur mesure.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Link to="/boutique" data-testid="hero-shop-btn" className="btn-primary rounded-full px-7 py-3 font-bold inline-flex items-center gap-2">
              Découvrir la boutique <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/devis" data-testid="hero-quote-btn" className="btn-blue rounded-full px-7 py-3 font-bold">
              Demander un devis
            </Link>
            <a href="tel:+33635307284" className="rounded-full border border-white/20 px-7 py-3 font-bold inline-flex items-center gap-2 hover:border-[#E6007E] transition-colors">
              <Phone className="w-5 h-5" /> 06 35 30 72 84
            </a>
          </div>
          <div data-testid="hero-info" className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm sm:text-base font-semibold">
            <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-address-link"
              className="inline-flex items-center gap-2 text-gray-100 hover:text-[#E6007E] transition-colors underline-offset-4 hover:underline">
              <MapPin className="w-4 h-4 text-[#E6007E]" /> 87 rue Centrale, 01230 Tenay
            </a>
            <span className="inline-flex items-center gap-2 text-gray-100"><Clock className="w-4 h-4 text-[#0055FF]" /> Du lundi au samedi, 9h00 – 19h00</span>
            <span data-testid="open-badge"
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-bold border ${openNow ? "bg-green-500/15 text-green-300 border-green-500/40" : "bg-red-500/15 text-red-300 border-red-500/40"}`}>
              <span className={`h-2 w-2 rounded-full ${openNow ? "bg-green-400" : "bg-red-400"}`} />
              {openNow ? "Ouvert" : "Fermé"}
            </span>
          </div>
        </div>
      </section>

      {/* LITHOPHANIE SHOWCASE (auto-rotating) */}
      <section className="relative overflow-hidden border-b border-white/10">
        <Splatter />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 grid gap-10 lg:grid-cols-2 items-center">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-[#E6007E]/40 bg-black/40 px-4 py-1.5 text-sm font-semibold text-[#E6007E]">
              Nos lithophanies
            </p>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-black">
              Vos photos, <span className="text-[#E6007E]">en lumière</span>
            </h2>
            <p className="mt-4 text-gray-300 max-w-md text-lg">
              Des souvenirs qui prennent vie une fois éclairés. Portraits, famille, animaux, paysages —
              transformez vos plus belles photos en objets uniques.
            </p>
            <Link to="/boutique?category=lithophanies" data-testid="showcase-litho-btn"
              className="mt-7 inline-flex btn-primary rounded-full px-7 py-3 font-bold items-center gap-2">
              Découvrir les lithophanies <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div data-testid="litho-showcase" className="relative rounded-2xl overflow-hidden border border-[#222] glow-fuchsia aspect-[4/3] bg-black">
            {[
              { img: "/images/litho_tigre_v2.jpg", label: "Lampe lithophanie « Tigre »" },
              { img: "/images/litho_creation_famille.jpg", label: "Lithophanie photo de famille" },
            ].map((s, i) => (
              <div key={i} className="absolute inset-0 transition-opacity duration-1000"
                style={{ opacity: slide === i ? 1 : 0 }}>
                <img src={s.img} alt={s.label} className="w-full h-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <span className="text-sm font-semibold text-white">{s.label}</span>
                </div>
              </div>
            ))}
            <div className="absolute top-3 right-3 flex gap-2 z-10">
              {[0, 1].map((i) => (
                <button key={i} onClick={() => setSlide(i)} aria-label={`Photo ${i + 1}`}
                  data-testid={`showcase-dot-${i}`}
                  className={`h-2.5 rounded-full transition-all ${slide === i ? "w-7 bg-[#E6007E]" : "w-2.5 bg-white/50"}`} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VIDEO — comment votre photo devient une création 3D */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="max-w-3xl">
          <h2 className="font-heading text-3xl sm:text-4xl font-black">
            Découvrez comment votre photo devient une <span className="text-[#E6007E]">création 3D</span>.
          </h2>
          <p className="mt-3 text-gray-400">De la photo à l'objet imprimé — suivez chaque étape de la création.</p>
        </div>
        <div className="mt-8">
          <HomeVideo />
        </div>
      </section>

      {/* GALERIE FRESQUE DE TENAY */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-[#0055FF]/40 bg-black/40 px-4 py-1.5 text-sm font-semibold text-[#0055FF]">
              Galerie
            </p>
            <h2 className="mt-4 font-heading text-2xl sm:text-4xl font-bold">
              Réalisation lithophanie — <span className="text-[#E6007E]">Fresque de Tenay</span>
            </h2>
            <p className="mt-2 text-gray-400">
              Une fresque lumineuse dédiée à Tenay et à sa faune. <span className="text-gray-300">(à usage non commercial)</span>
            </p>
          </div>
        </div>
        <div className="mt-8 grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {FRESQUE.map((src, i) => (
            <div key={i} data-testid={`fresque-${i}`}
              className="card-hover relative overflow-hidden rounded-2xl border border-[#222] bg-black aspect-[3/4]">
              <div className="absolute inset-0">
                <img src={src} alt={`Fresque de Tenay ${i + 1} — à usage non commercial`}
                  className="w-full h-full object-cover" />
              </div>
              <span className="absolute bottom-2 left-2 rounded-full bg-black/70 px-2 py-0.5 text-[10px] text-gray-300">
                à usage non commercial
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="font-heading text-2xl sm:text-4xl font-bold">Nos catégories</h2>
        <p className="mt-2 text-gray-400">Explorez tout ce que nous pouvons créer pour vous.</p>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {cats.map((c, i) => {
            const meta = CAT_META[c.slug] || {};
            const Icon = meta.icon || Boxes;
            const to = meta.to || `/boutique?category=${c.slug}`;
            return (
              <Link key={c.slug} to={to} data-testid={`home-cat-${c.slug}`}
                className="group card-hover fade-up relative rounded-2xl overflow-hidden border border-[#222] bg-[#0A0A0A] min-h-[220px] flex items-end"
                style={{ animationDelay: `${i * 70}ms` }}>
                <img src={meta.img} alt={c.name}
                  className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                <div className="relative p-6">
                  <Icon className="w-8 h-8 text-[#E6007E]" />
                  <h3 className="mt-3 font-heading text-xl font-bold text-white">{c.name}</h3>
                  <p className="text-sm text-gray-300">{meta.desc}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* WHY US */}
      <section className="relative overflow-hidden bg-[#0A0A0A] border-y border-white/10">
        <Splatter />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 grid gap-10 lg:grid-cols-2 items-center">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black">
              À <span className="text-[#E6007E]">Tenay</span>, vos idées <span className="text-[#0055FF]">prennent vie</span>
            </h2>
            <ul className="mt-8 space-y-4">
              {ARGUMENTS.map((a) => (
                <li key={a} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-[#E6007E] shrink-0 mt-0.5" />
                  <span className="text-gray-200 text-lg">{a}</span>
                </li>
              ))}
            </ul>
            <Link to="/devis" className="mt-9 inline-flex btn-primary rounded-full px-7 py-3 font-bold">Lancer mon projet</Link>
          </div>
          <div className="rounded-2xl overflow-hidden border border-[#222] glow-blue">
            <img src="/images/custom_object.jpeg" alt="Objet 3D fuchsia et bleu" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="font-heading text-2xl sm:text-4xl font-bold">Produits populaires</h2>
              <p className="mt-2 text-gray-400">Une sélection de nos créations prêtes à commander.</p>
            </div>
            <Link to="/boutique" className="hidden sm:inline-flex items-center gap-1 text-[#E6007E] font-bold hover:gap-2 transition-all">
              Tout voir <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-8 grid gap-6 grid-cols-2 lg:grid-cols-4">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
